<template>
	<v-slide-y-transition>
		<div
			v-if="showBanner"
			class="status-banner"
			:class="{ 'status-banner--mobile': $vuetify.breakpoint.xs }"
			:style="bannerStyle"
		>
			<v-icon color="white" class="banner-icon">{{ bannerIcon }}</v-icon>
			<span v-if="countdownText" class="countdown-badge">{{ countdownText }}</span>
			<span class="banner-text">{{ status.bannerText }}</span>
			<v-btn
				v-if="status.dismissable"
				icon
				small
				color="white"
				class="dismiss-btn"
				@click="dismissed = true"
			>
				<v-icon small>mdi-close</v-icon>
			</v-btn>
		</div>
	</v-slide-y-transition>
</template>

<script>
const STATUS_CONFIG = {
	operational: { color: "success", hex: "#4caf50", icon: "mdi-check-circle" },
	maintenance: { color: "primary", hex: "#3C41C3", icon: "mdi-wrench" },
	degraded: { color: "warning", hex: "#FFC107", icon: "mdi-speedometer-slow" },
	partial_outage: { color: "deep-orange", hex: "#ff5722", icon: "mdi-alert" },
	major_outage: { color: "error", hex: "#FF5252", icon: "mdi-alert-octagon" }
};

export default {
	name: "StatusBanner",

	props: {
		status: {
			type: Object,
			required: true
		},

		mini: {
			type: Boolean,
			default: true
		}
	},

	data() {
		return {
			dismissed: false
		};
	},

	computed: {
		showBanner() {
			return (
				this.status?.enabled &&
				this.status?.effectiveBanner &&
				this.status?.bannerText &&
				!this.dismissed
			);
		},

		bannerColor() {
			const config = STATUS_CONFIG[this.status?.status] || STATUS_CONFIG.operational;

			return config.color;
		},

		bannerColorHex() {
			const config = STATUS_CONFIG[this.status?.status] || STATUS_CONFIG.operational;

			return config.hex;
		},

		bannerIcon() {
			const config = STATUS_CONFIG[this.status?.status] || STATUS_CONFIG.operational;

			return config.icon;
		},

		countdownText() {
			if (this.status?.isInMaintenanceWindow && this.status?.maintenanceRemaining) {
				const mins = this.status.maintenanceRemaining;

				return mins === 1
					? `1 ${this.$t("minute-remaining")}`
					: `${mins} ${this.$t("minutes-remaining")}`;
			}
			if (this.status?.isInAlertWindow && this.status?.maintenanceCountdown) {
				const mins = this.status.maintenanceCountdown;

				return mins === 1
					? `${this.$t("starts-in")} 1 ${this.$t("minute")}`
					: `${this.$t("starts-in")} ${mins} ${this.$t("minutes")}`;
			}

			return null;
		},

		bannerStyle() {
			const style = { backgroundColor: this.bannerColorHex };
			const menuWidth = this.mini ? "56px" : "256px";

			// On desktop, use margin-left for sticky positioning
			// On mobile (xs), use left for fixed positioning
			if (this.$vuetify.breakpoint.xs) {
				style.left = menuWidth;
			} else {
				style.marginLeft = menuWidth;
			}

			return style;
		}
	},

	watch: {
		status: {
			deep: true,

			handler() {
				// Reset dismissed state when status changes
				this.dismissed = false;
			}
		}
	}
};
</script>

<style scoped>
.status-banner {
	position: sticky;
	top: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	padding: 10px 16px;
	color: white;
	font-weight: 600;
	font-size: 14px;
	transition: margin-left 0.2s ease;
	gap: 8px;
}

.status-banner--mobile {
	position: fixed;
	right: 0;
	margin-left: 0 !important;
	transition: left 0.2s ease;
}

.banner-icon {
	flex-shrink: 0;
}

.banner-text {
	flex: 1;
	line-height: 1.4;
}

.countdown-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.2);
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
	flex-shrink: 0;
	align-self: center;
}

.dismiss-btn {
	opacity: 0.8;
	flex-shrink: 0;
}

.dismiss-btn:hover {
	opacity: 1;
}
</style>
